import PropTypes from 'prop-types'
import React from 'react'
import cc from 'classcat'

import { AddToCartNotice } from '../../templateComponents/Buybox'
import { AmazonButton, CheckoutButton, PaypalButton } from '../../templateComponents/Cart/CheckoutButtons'
import CartEmptyNote from '../epages.base/CartEmptyNote'
import CartTemplateComponent from '../../templateComponents/Cart/Cart'
import Coupons from '../../templateComponents/Cart/Coupons'
import EnergyLabel from '../../templateComponents/EnergyLabel'
import LazyImage from '../../LazyImage'
import LineItem from '../../templateComponents/Cart/LineItem'
import LineItems from '../../templateComponents/Cart/LineItems'
import Link from '../../templateComponents/Link'
import PlaceholderImage from '../../PlaceholderImage'

export default function Cart({ cart, displayWishlistError, trackProductClick, shop, t, invalidProducts }) {
  return (
    <CartTemplateComponent>
      <div>
        {!cart.totalNumberOfItems && <CartEmptyNote shopHref={shop.href} t={t} />}

        {cart.totalNumberOfItems > 0 && (
          <div className="cart">
            <section className="section">
              <div className="wrapper">
                <div className="content">
                  <h1 className="cart-headline">{t('views.storefrontView.basketPageSection.title')}</h1>

                  {cart.quantityMismatch && (
                    <div className="cart-warning-message">
                      {t('views.storefrontView.basketPageSection.productQuantityTooHighExplanation')}
                    </div>
                  )}
                  {cart.couponSuccess && !cart.coupon.hasValidationErrors && (
                    <div className="cart-success-message">
                      {t('views.storefrontView.basketPageSection.couponInputField.successMessage')}
                    </div>
                  )}
                  {cart.checkoutState && !cart.checkoutState.priceValidToOrder && (
                    <div className="cart-warning-message">
                      {t('views.storefrontView.basketPageSection.checkoutValidation.cartMinimumAmountNotReached', {
                        amount: cart.minimumOrderValue.formatted,
                      })}
                    </div>
                  )}
                  {displayWishlistError && (
                    <div className="cart-warning-message">
                      {t('views.storefrontView.basketPageSection.wishlistItemsNotAddedCompletely')}
                    </div>
                  )}
                  {invalidProducts.length > 0 && (
                    <div className="cart-warning-message">
                      <span>
                        {t('views.storefrontView.basketPageSection.invalidProducts')}
                        <ul>
                          {invalidProducts.map((item) => (
                            <li key={`ip-${item.name}`}>{item.name}</li>
                          ))}
                        </ul>
                      </span>
                    </div>
                  )}
                  <Coupons.ValidationInfo errorClass="cart-error-message" warningClass="cart-warning-message" />

                  <table className="cart-table">
                    <thead className="cart-table-head">
                      <tr>
                        <th className="cart-table-head-product">{t('components.productComponent.product')}</th>
                        <th className="cart-table-head-description"></th>
                        <th className="cart-table-head-price">
                          {t('views.storefrontView.basketPageSection.singlePrice')}
                        </th>
                        <th className="cart-table-head-quantity">
                          {t('views.storefrontView.basketPageSection.quantity')}
                        </th>
                        <th className="cart-table-head-total">
                          {t('views.storefrontView.basketPageSection.totalPrice')}
                        </th>
                        <th className="cart-table-head-actions"></th>
                      </tr>
                    </thead>
                    <LineItems
                      tagName="tbody"
                      renderItem={(lineItem, lineItemIndex) => (
                        <tr>
                          <td className="cart-table-item-product">
                            <Link to={lineItem.productUrl} onClick={() => trackProductClick(lineItem, lineItemIndex)}>
                              {lineItem.imageUrl ? (
                                <LazyImage src={lineItem.imageUrl} className="cart-table-item-product-image" />
                              ) : (
                                <PlaceholderImage className="cart-table-item-product-image" />
                              )}
                            </Link>
                          </td>
                          <td className="cart-table-item-description">
                            <h2 className="cart-table-item-description-headline">
                              <Link to={lineItem.productUrl} onClick={() => trackProductClick(lineItem, lineItemIndex)}>
                                {lineItem.name}
                              </Link>
                            </h2>
                            {lineItem.variationString && (
                              <p className="cart-table-item-description-variation-values">{lineItem.variationString}</p>
                            )}
                            {lineItem.essentialFeatures.length > 0 && (
                              <p className="cart-table-item-description-text">
                                {lineItem.essentialFeatures.map((essentialFeature, essentialFeatureIndex) => (
                                  <span key={essentialFeatureIndex}>
                                    {essentialFeature}
                                    <br />
                                  </span>
                                ))}
                              </p>
                            )}
                            {lineItem.energyLabel && (
                              <EnergyLabel
                                t={t}
                                energyLabel={lineItem.energyLabel}
                                energyLabelSourceFile={lineItem.energyLabelSourceFile}
                              />
                            )}

                            {!shop.beyond && lineItem.productId && (
                              <LineItem.AddToWishlistLinkInCart
                                className="add-wishlist-link add-wishlist-icon"
                                title={t('views.storefrontView.basketPageSection.addToWishlistLinkInCart.label')}
                              >
                                <span className="add-wishlist-text">
                                  {t('views.storefrontView.basketPageSection.addToWishlistLinkInCart.label')}
                                </span>
                              </LineItem.AddToWishlistLinkInCart>
                            )}
                          </td>

                          <td className="cart-table-item-price">
                            <span className="show-subhead">
                              {t('views.storefrontView.basketPageSection.singlePrice')}
                            </span>
                            <span className="price-value">{lineItem.singleItemPrice.formatted}</span>
                          </td>
                          <td className="cart-table-item-quantity">
                            <span className="show-subhead">{t('views.storefrontView.basketPageSection.quantity')}</span>
                            <LineItem.QuantityField
                              min={0}
                              invalidProduct={lineItem.productId === null}
                              className="quantity-field"
                            />
                          </td>
                          <td className="cart-table-item-total">
                            <span className="show-subhead">
                              {t('views.storefrontView.basketPageSection.totalPrice')}
                            </span>
                            <span
                              className={cc(['price-value', { 'coupon-indicator': lineItem.lineItemCouponDiscount }])}
                            >
                              {lineItem.lineItemPrice.formatted}
                            </span>
                          </td>
                          <td className="cart-table-item-actions">
                            {!shop.beyond && lineItem.productId && (
                              <LineItem.AddToWishlistLinkInCart
                                className="add-wishlist-link add-wishlist-icon"
                                title={t('views.storefrontView.basketPageSection.addToWishlistLinkInCart.label')}
                              >
                                <span className="add-wishlist-text">
                                  {t('views.storefrontView.basketPageSection.addToWishlistLinkInCart.label')}
                                </span>
                              </LineItem.AddToWishlistLinkInCart>
                            )}

                            <LineItem.RemoveLineItemButton
                              className="remove-link remove-icon"
                              title={t('views.storefrontView.basketPageSection.removeProductButton.label')}
                            >
                              <span className="remove-text">
                                {t('views.storefrontView.basketPageSection.removeProductButton.label')}
                              </span>
                            </LineItem.RemoveLineItemButton>
                          </td>
                        </tr>
                      )}
                    ></LineItems>
                  </table>

                  {cart.canHaveCoupon && (
                    <Coupons.RedemptionForm className="coupon-redemption-form">
                      <label htmlFor="coupon-form-field" className="coupon-redemption-form-label">
                        {t('views.storefrontView.basketPageSection.couponInputField.label')}
                      </label>
                      <div>
                        <Coupons.RedemptionForm.CouponField
                          id="coupon-form-field"
                          className="coupon-redemption-form-field"
                          errorClass="invalid"
                        />
                        <Coupons.RedemptionForm.RedeemButton className="coupon-redemption-form-button">
                          <span className="hide-text">
                            {t('views.storefrontView.basketPageSection.couponInputField.redeemButton.label')}
                          </span>
                        </Coupons.RedemptionForm.RedeemButton>
                      </div>
                      <Coupons.CouponError
                        className="invalid-error-message"
                        hiddenClass="invalid-error-message-hidden"
                      />
                    </Coupons.RedemptionForm>
                  )}

                  <div className="cart-totals">
                    <table className="cart-totals-table">
                      <tbody>
                        <tr>
                          <td className="sub-total-title">{t('views.storefrontView.basketPageSection.subtotal')}</td>
                          <td className="sub-total">{cart.subAmount}</td>
                        </tr>
                        {cart.coupon && (
                          <>
                            <tr className="cart-coupon">
                              <td className="coupon-title">
                                <div className="cart-coupon-campaign">
                                  <div className="cart-coupon-campaign-label">
                                    {t('views.storefrontView.basketPageSection.coupon')}
                                  </div>
                                  <span className="cart-coupon-campaign-name">
                                    {cart.couponCampaign.name}{' '}
                                    <span className="type">({cart.couponCampaign.type.formatted})</span>
                                  </span>
                                </div>
                              </td>
                              <td className="coupon-price">
                                {typeof cart.coupon.lineItemPrice.amount === 'number' && (
                                  <span className="cart-coupon-campaign-price">
                                    {cart.coupon.lineItemPrice.formatted}
                                  </span>
                                )}
                                <Coupons.RemoveCouponButton className="cart-coupon-campaign-remove-icon"></Coupons.RemoveCouponButton>
                              </td>
                            </tr>
                            <tr className="cart-coupon-campaign-remove-link">
                              <td>
                                <Coupons.RemoveCouponButton className="remove-link remove-icon">
                                  <span className="remove-text">
                                    {t('views.storefrontView.basketPageSection.couponInputField.removeButton.label')}
                                  </span>
                                </Coupons.RemoveCouponButton>
                              </td>
                              <td></td>
                            </tr>
                          </>
                        )}
                        {cart.shippingLineItem && (
                          <tr>
                            <td className="shipping-price-title">{cart.shippingLineItem.shippingMethod.name}</td>
                            <td className="shipping-price">{cart.shippingLineItem.lineItemPrice.formatted}</td>
                          </tr>
                        )}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td className="grand-amount-title">{t('views.storefrontView.basketPageSection.total')}</td>
                          <td className="grand-amount">{cart.grandAmount}</td>
                        </tr>
                        <tr>
                          <td colSpan="2" className="grand-amount-note">
                            {t(cart.grandAmountNote, { nsSeparator: '__' })}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                    <div id="ep-app-area-cart-view-cart-totals"></div>
                    <CheckoutButton className="cart-totals-button-checkout">
                      <span className="checkout-button-icon"></span>
                      {t('views.storefrontView.basketPageSection.checkoutLinkButton.label')}
                    </CheckoutButton>
                    <div className="checkout-buttons column">
                      <PaypalButton className="paypal-button" shape="rect" color="gold" />
                      <AmazonButton className="amazon-button" />
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        )}
      </div>
      <AddToCartNotice />
    </CartTemplateComponent>
  )
}

Cart.propTypes = {
  t: PropTypes.func.isRequired,
  cart: PropTypes.object.isRequired,
  shop: PropTypes.object.isRequired,
  displayWishlistError: PropTypes.bool.isRequired,
  trackProductClick: PropTypes.func.isRequired,
  invalidProducts: PropTypes.arrayOf(PropTypes.object).isRequired,
}
